import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler';

const DrugScreeningFile = () => {
    const { guest_id } = useParams();
    const navigate = useNavigate();
    const [guestFiles, setGuestFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [filter, setFilter] = useState({ year: '', month: '' });
    const monthList = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Initialize filter with current year/month
    useEffect(() => {
        const now = new Date();
        setFilter({
            year: now.getFullYear(),
            month: now.getMonth() + 1
        });
    }, []);

    // Fetch uploads whenever guest_id, year, or month changes
    useEffect(() => {
        if (guest_id && filter.year && filter.month) {
            fetchUploads();
        }
    }, [guest_id, filter.year, filter.month]);

    const fetchUploads = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-drug-screening`, {
                params: {
                    guest_id,
                    year: filter.year,
                    month: filter.month,
                },
                withCredentials: true,
            });

            if (res.data.status) {
                // Parse JSON strings into arrays for each week
                const data = res.data.data.map(guest => {
                    const parsedGuest = { ...guest };
                    for (let week = 1; week <= 5; week++) {
                        const key = `week${week}_file`;
                        try {
                            const arr = guest[key] ? JSON.parse(guest[key]) : [];
                            parsedGuest[`week${week}_files`] = arr.map(f => ({
                                filename: typeof f.filename === "string"
                                    ? f.filename
                                    : f.filename?.filename, // extract multer filename
                                day: f.day || ""
                            }));
                        } catch (err) {
                            parsedGuest[`week${week}_files`] = [];
                        }
                    }
                    return parsedGuest;
                });

                setGuestFiles(data);
            } else {
                setGuestFiles([]);
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter((prev) => ({ ...prev, [name]: value }));
    };

    const clearFilter = () => {
        const now = new Date();
        setFilter({
            year: now.getFullYear(),
            month: now.getMonth() + 1
        });
    };


        const handleDeleteFile = async (guestId, week, filename) => {
            if (!window.confirm("Are you sure you want to delete this file?")) return;
            try {
                const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/delete-drug-screening-file`, {
                    guest_id: guestId,
                    year: filter.year,
                    month: filter.month,
                    week,
                    filename
                }, { withCredentials: true });
    
                if (res.data.status) {
                    fetchUploads(); // refresh after deletion
                } else {
                    alert(res.data.message || "Failed to delete file.");
                }
            } catch (err) {
                globalErrorHandler(err, navigate);
            }
        };
    return (
        <div className="container-fluid mt-4">
            <h3 className="mb-4">Guest Drug Screening Results</h3>

            <div className="card">
                <div className="card-body table-responsive">
                    <div className="row g-3 mb-3">
                        <div className="col-md-2">
                            <label className="form-label">Year</label>
                            <select className="form-select" name="year" value={filter.year} onChange={handleFilterChange}>
                                {Array.from({ length: 30 }, (_, i) => {
                                    const year = 2025 + i;
                                    return <option key={year} value={year}>{year}</option>;
                                })}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <label className="form-label">Month</label>
                            <select className="form-select" name="month" value={filter.month} onChange={handleFilterChange}>
                                {monthList.map((m, i) => (
                                    <option key={i} value={i + 1}>{m}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <label className="form-label">&nbsp;</label>
                            <button type="button" className="btn btn-dark w-100 rounded-3" onClick={clearFilter}>Clear</button>
                        </div>
                    </div>

                    <table className="table table-bordered">
                        <thead className="table-success">
                            <tr>
                                <th>Guest ID</th>
                                <th>Name</th>
                                <th>Contact Info</th>
                                {[1, 2, 3, 4, 5].map(week => (
                                    <th key={week}>Week {week}</th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="8">Loading...</td></tr>
                            ) : guestFiles.length === 0 ? (
                                <tr><td colSpan="8">No data found.</td></tr>
                            ) : (
                                guestFiles.map((guest) => (
                                    <tr key={guest.id}>
                                        <td>{`BBM${String(guest.id).padStart(4, '0')}`}</td>
                                        <td>{guest.name}</td>
                                        <td>{`${guest.phone ? guest.phone + ' ' : ''}${guest.email}`}</td>
                                        {[1, 2, 3, 4, 5].map((week) => {
                                            const files = guest[`week${week}_files`] || [];
                                            return (
                                                <td key={week}>
                                                    {files.length > 0 ? (
                                                        files.map((file, idx) => (
                                                            <div key={idx} className="mb-1 d-flex align-items-center">
                                                                <a
                                                                    href={`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/download-drug-screening/${file.filename}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-success me-1"
                                                                >
                                                                    <i className="bi bi-download me-1"></i> File {idx + 1} {file.day ? `- ${file.day}` : ""}
                                                                </a>
                                                                <button
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => handleDeleteFile(guest.guest_id, week, file.filename)}
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className="text-muted">No File</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DrugScreeningFile;
